/**
 * ActivityFeed - 실시간 활동 피드 컴포넌트
 * WebSocket을 통해 실시간으로 활동 로그를 표시
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import activityWebSocket from '../services/activityWebSocket';
import { getCategoryLabel, getActionTypeLabel, getStatusInfo } from '../services/activityLogService';

const ActivityFeed = ({ maxItems = 50, filters = {} }) => {
  const { token } = useAuth();
  const [activities, setActivities] = useState([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [paused, setPaused] = useState(false);
  const feedRef = useRef(null);

  // WebSocket 연결
  useEffect(() => {
    if (!token) return;

    // 연결 이벤트 핸들러
    const handleConnected = () => {
      setConnected(true);
      setError(null);
    };

    const handleDisconnected = ({ code, reason }) => {
      setConnected(false);
      if (code !== 1000) {
        setError(`연결 끊김: ${reason || '알 수 없는 오류'}`);
      }
    };

    const handleError = ({ error }) => {
      setError('연결 오류가 발생했습니다');
      console.error('[ActivityFeed] WebSocket error:', error);
    };

    const handleActivity = (activity) => {
      if (paused) return;

      setActivities(prev => {
        const newActivities = [activity, ...prev];
        // 최대 개수 유지
        if (newActivities.length > maxItems) {
          return newActivities.slice(0, maxItems);
        }
        return newActivities;
      });
    };

    const handleAnomaly = (alert) => {
      // 이상 알림은 항상 표시 (일시정지 무시)
      console.warn('[ActivityFeed] Anomaly alert:', alert);
      // TODO: 알림 UI 표시
    };

    // 이벤트 리스너 등록
    activityWebSocket.on('connected', handleConnected);
    activityWebSocket.on('disconnected', handleDisconnected);
    activityWebSocket.on('error', handleError);
    activityWebSocket.on('activity', handleActivity);
    activityWebSocket.on('anomaly', handleAnomaly);

    // WebSocket 연결
    activityWebSocket.connect(token);

    // 필터 적용
    if (Object.keys(filters).length > 0) {
      activityWebSocket.subscribe(filters);
    }

    // Cleanup
    return () => {
      activityWebSocket.off('connected', handleConnected);
      activityWebSocket.off('disconnected', handleDisconnected);
      activityWebSocket.off('error', handleError);
      activityWebSocket.off('activity', handleActivity);
      activityWebSocket.off('anomaly', handleAnomaly);
      activityWebSocket.disconnect();
    };
  }, [token, maxItems, paused, filters]);

  // 필터 변경 시 구독 업데이트
  useEffect(() => {
    if (connected && Object.keys(filters).length > 0) {
      activityWebSocket.subscribe(filters);
    }
  }, [connected, filters]);

  // 상대 시간 포맷
  const formatRelativeTime = useCallback((timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);

    if (diffSec < 5) return '방금';
    if (diffSec < 60) return `${diffSec}초 전`;
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;

    return time.toLocaleString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // 카테고리 아이콘
  const getCategoryIcon = (category) => {
    const icons = {
      auth: '🔐',
      content: '📝',
      interaction: '💬',
      settings: '⚙️',
      activity: '⭐',
      ai: '🤖',
      admin: '👑'
    };
    return icons[category] || '📋';
  };

  // 활동 목록 초기화
  const clearActivities = () => {
    setActivities([]);
  };

  // 일시정지 토글
  const togglePause = () => {
    setPaused(prev => !prev);
  };

  return (
    <div className="flex flex-col bg-white border border-gray-200 rounded-xl overflow-hidden h-full min-h-[400px] max-h-[600px]">
      {/* 헤더 */}
      <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full transition-colors duration-300 ${connected ? 'bg-green-500 shadow-[0_0_4px_rgba(34,197,94,0.5)]' : 'bg-red-500 animate-pulse'}`} />
          <h3 className="m-0 text-[15px] font-semibold text-gray-900">실시간 활동</h3>
          {activities.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-gray-200 rounded-full text-[11px] font-semibold text-gray-600">{activities.length}</span>
          )}
        </div>
        <div className="flex gap-1">
          <button
            className={`flex items-center justify-center w-8 h-8 bg-transparent border-none rounded-md cursor-pointer transition-colors text-sm hover:bg-gray-200 ${paused ? 'bg-amber-100' : ''}`}
            onClick={togglePause}
            title={paused ? '재개' : '일시정지'}
          >
            {paused ? '▶️' : '⏸️'}
          </button>
          <button
            className="flex items-center justify-center w-8 h-8 bg-transparent border-none rounded-md cursor-pointer transition-colors text-sm hover:bg-gray-200"
            onClick={clearActivities}
            title="목록 초기화"
          >
            🗑️
          </button>
        </div>
      </div>

      {/* 상태 메시지 */}
      {error && (
        <div className="py-2 px-4 text-xs bg-red-100 text-red-700">
          ⚠️ {error}
        </div>
      )}

      {paused && (
        <div className="py-2 px-4 text-xs bg-amber-100 text-amber-800">
          ⏸️ 일시정지됨 - 새 활동이 기록되지 않습니다
        </div>
      )}

      {/* 활동 목록 */}
      <div className="flex-1 overflow-y-auto py-2" ref={feedRef}>
        {activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center text-gray-400">
            <span className="text-5xl mb-4 opacity-50">📭</span>
            <p className="m-0 text-sm">아직 새 활동이 없습니다</p>
            <p className="mt-1 text-xs text-gray-300">사용자 활동이 발생하면 여기에 표시됩니다</p>
          </div>
        ) : (
          activities.map((activity, index) => (
            <div
              key={activity.id || index}
              className={`flex items-start gap-3 py-3 px-4 transition-colors border-l-[3px] border-transparent hover:bg-gray-50 ${index === 0 ? 'animate-slideIn bg-blue-50 border-l-blue-500' : ''}`}
            >
              <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg text-base">
                {getCategoryIcon(activity.category)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-1 mb-1">
                  <span className="font-semibold text-gray-900 text-[13px]">{activity.user?.display_name || '알 수 없음'}</span>
                  <span className="text-gray-600 text-[13px]">{activity.summary || getActionTypeLabel(activity.action_type)}</span>
                </div>
                <div className="flex items-center gap-1 text-[11px] text-gray-400">
                  <span className="py-0.5 px-1.5 bg-gray-100 rounded">{getCategoryLabel(activity.category)}</span>
                  {activity.service && activity.service !== 'unknown' && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className="text-blue-500">{activity.service}</span>
                    </>
                  )}
                  <span className="text-gray-300">·</span>
                  <span className="text-gray-300">{formatRelativeTime(activity.timestamp)}</span>
                </div>
              </div>
              {activity.exp_granted > 0 && (
                <div className="flex-shrink-0 py-0.5 px-2 bg-green-100 text-green-600 rounded text-[11px] font-semibold">+{activity.exp_granted}</div>
              )}
            </div>
          ))
        )}
      </div>

      {/* 연결 상태 */}
      <div className="py-2 px-4 border-t border-gray-100 bg-gray-50">
        <span className={`text-[11px] ${connected ? 'text-green-500' : 'text-red-500'}`}>
          {connected ? '연결됨' : '연결 중...'}
        </span>
      </div>
    </div>
  );
};

export default ActivityFeed;
